// Seller Users screen strings — EN + TR.
// Source: features/settings/SellerTeam.tsx.

import type { LocalizedDict } from '../index'

type SellerTeamStrings = {
  title: string
  invite: string
  roles: { owner: string; staff: string }
  status: { active: string; invited: string }
  invitedAgo: (when: string) => string
  actions: { resend: string; revoke: string; remove: string }
  columns: {
    name: string
    email: string
    role: string
    status: string
    prices: string
    actions: string
  }
  prices: {
    visible: string
    hidden: string
    updating: string
    lockedByPexify: string
    show: (who: string) => string
    hide: (who: string) => string
  }
  modal: {
    title: string
    email: string
    roleNote: string
    hidePrices: string
    hidePricesNote: string
    send: string
    sending: string
    cancel: string
  }
  errors: { emailRegistered: string; pending: string; generic: string }
  toasts: {
    invited: string
    resent: string
    revoked: string
    removed: string
    emailFailed: string
    pricesHidden: string
    pricesShown: string
  }
  staffNote: string
  removeConfirm: { title: string; body: (who: string) => string; confirm: string }
  loading: string
  loadFailed: string
  empty: string
  notAvailable: string
}

export const sellerTeamI18n: LocalizedDict<SellerTeamStrings> = {
  en: {
    title: 'Users',
    invite: 'Invite user',
    roles: { owner: 'Owner', staff: 'Staff' },
    status: { active: 'Active', invited: 'Invitation pending' },
    invitedAgo: (when) => `Invited ${when}`,
    actions: { resend: 'Resend', revoke: 'Cancel invite', remove: 'Remove access' },
    columns: {
      name: 'Name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      prices: 'Prices',
      actions: 'Actions',
    },
    prices: {
      visible: 'Visible',
      hidden: 'Hidden',
      updating: 'Updating…',
      lockedByPexify: 'Hidden by PEXIFY',
      show: (who) => `Show prices to ${who}`,
      hide: (who) => `Hide prices from ${who}`,
    },
    modal: {
      title: 'Invite a teammate',
      email: 'Email',
      roleNote:
        'Staff can manage orders, products, messages and reports. Money, store connections, settings and invites stay with the Owner.',
      hidePrices: 'Hide prices',
      hidePricesNote:
        'They see the catalog without product prices; orders, wallet and every other amount stay visible. You can turn prices on later from this list.',
      send: 'Send invite',
      sending: 'Sending…',
      cancel: 'Cancel',
    },
    errors: {
      emailRegistered: 'This email already has a PEXIFY account. Use a different email.',
      pending: 'An invitation is already pending for this email.',
      generic: 'Something went wrong. Try again.',
    },
    toasts: {
      invited: 'Invitation sent',
      resent: 'Invitation sent again',
      revoked: 'Invitation cancelled',
      removed: 'Access removed',
      emailFailed: "Invitation saved, but the email couldn't be sent. Try Resend.",
      pricesHidden: 'Prices hidden',
      pricesShown: 'Prices visible',
    },
    staffNote: 'Only the Owner can invite or remove users.',
    removeConfirm: {
      title: 'Remove access?',
      body: (who) => `${who} will no longer be able to sign in to this account.`,
      confirm: 'Remove access',
    },
    loading: 'Loading users…',
    loadFailed: 'Could not load users. Try again.',
    empty: 'No users yet.',
    notAvailable: "Team management isn't available for this account.",
  },
  tr: {
    title: 'Kullanıcılar',
    invite: 'Kullanıcı davet et',
    roles: { owner: 'Sahip', staff: 'Çalışan' },
    status: { active: 'Aktif', invited: 'Davet bekliyor' },
    invitedAgo: (when) => `${when} davet edildi`,
    actions: { resend: 'Yeniden gönder', revoke: 'Daveti iptal et', remove: 'Erişimi kaldır' },
    columns: {
      name: 'Ad',
      email: 'E-posta',
      role: 'Rol',
      status: 'Durum',
      prices: 'Fiyatlar',
      actions: 'Eylemler',
    },
    prices: {
      visible: 'Görünür',
      hidden: 'Gizli',
      updating: 'Güncelleniyor…',
      lockedByPexify: "PEXIFY gizledi",
      show: (who) => `${who} fiyatları görsün`,
      hide: (who) => `${who} fiyatları görmesin`,
    },
    modal: {
      title: 'Ekip arkadaşı davet et',
      email: 'E-posta',
      roleNote:
        "Çalışan siparişleri, ürünleri, mesajları ve raporları yönetir. Para, mağaza bağlama, ayarlar ve davet Sahip'te kalır.",
      hidePrices: 'Fiyatları gizle',
      hidePricesNote:
        'Kataloğu ürün fiyatları olmadan görür; siparişler, cüzdan ve diğer tüm tutarlar görünür kalır. Fiyatları sonra bu listeden açabilirsiniz.',
      send: 'Davet gönder',
      sending: 'Gönderiliyor…',
      cancel: 'Vazgeç',
    },
    errors: {
      emailRegistered: 'Bu e-posta başka bir PEXIFY hesabında kayıtlı. Farklı bir e-posta kullanın.',
      pending: 'Bu e-postaya zaten bekleyen bir davet var.',
      generic: 'Bir şey ters gitti. Tekrar deneyin.',
    },
    toasts: {
      invited: 'Davet gönderildi',
      resent: 'Davet yeniden gönderildi',
      revoked: 'Davet iptal edildi',
      removed: 'Erişim kaldırıldı',
      emailFailed: "Davet kaydedildi ama e-posta gönderilemedi. Yeniden gönder'i deneyin.",
      pricesHidden: 'Fiyatlar gizlendi',
      pricesShown: 'Fiyatlar açıldı',
    },
    staffNote: "Kullanıcı davet etme ve kaldırma yalnız Sahip'te.",
    removeConfirm: {
      title: 'Erişim kaldırılsın mı?',
      body: (who) => `${who} artık bu hesaba giriş yapamayacak.`,
      confirm: 'Erişimi kaldır',
    },
    loading: 'Kullanıcılar yükleniyor…',
    loadFailed: 'Kullanıcılar yüklenemedi. Tekrar deneyin.',
    empty: 'Henüz kullanıcı yok.',
    notAvailable: 'Ekip yönetimi bu hesap için kullanılamıyor.',
  },
}
